import * as React from 'react';
import { MouseEventHandler } from 'react';
import { Avatar, Label, Link, UIHeading5, UIText2, useDeviceType } from '@cian/ui-kit';
import { TColors } from '@cian/ui-kit-design-tokens/colors';
import { IconActionChevronRight12 } from '@cian/ui-kit-design-tokens/icons';

import { clsx } from 'clsx';
import * as styles from './PromoOfferCard.css';
import { CallButton } from '../../containers/CallButton';

interface IPromoOfferCardProps {
  offerUrl: string;
  backgroundImage: string;
  location: React.ReactNode;
  favoriteButton: React.ReactNode;
  logoUrl?: string;
  newbuildingName: string;
  newbuildingUrl: string;
  promoTitle: string;
  price: React.ReactNode;
  description: string;
  onPromoLabelClick: MouseEventHandler<HTMLDivElement>;
  trackCardClick(): void;
  trackCallClick(): void;
  trackNewbuildingClick(): void;
}

export const PromoOfferCard: React.FC<IPromoOfferCardProps> = ({
  backgroundImage,
  location,
  logoUrl,
  newbuildingName,
  newbuildingUrl,
  favoriteButton,
  promoTitle,
  price,
  description,
  onPromoLabelClick,
  offerUrl,
  trackCardClick,
  trackNewbuildingClick,
  trackCallClick,
}) => {
  const device = useDeviceType();
  const isDesktop = React.useMemo(() => device === 'desktop', [device]);

  const [hovered, setHover] = React.useState(false);
  const [clicked, setClicked] = React.useState(false);
  const [labelColor, setLabelColor] = React.useState<TColors>('surface-positive-default');

  const onLabelEnter = React.useCallback(() => {
    setLabelColor('surface-positive-hovered');
  }, []);

  const onLabelLeave = React.useCallback(() => {
    setLabelColor('surface-positive-default');
  }, []);

  const onLabelClick: MouseEventHandler<HTMLDivElement> = React.useCallback(
    event => {
      setLabelColor('surface-positive-pressed');
      onPromoLabelClick(event);
    },
    [onPromoLabelClick],
  );

  const onMouseEnter = React.useCallback(() => {
    if (clicked || !isDesktop) {
      return;
    }

    setHover(true);
  }, [clicked, isDesktop]);

  const onMouseLeave = React.useCallback(() => {
    if (clicked || !isDesktop) {
      return;
    }

    setHover(false);
  }, [clicked, isDesktop]);

  const onCallButtonClick = React.useCallback(() => {
    setClicked(true);

    trackCallClick();
  }, [trackCallClick]);

  return (
    <div className={styles['wrapper']} data-testid="PromoOfferCardWrapper">
      <div
        className={clsx(styles['container'], hovered && styles['container--hovered'])}
        onClick={trackCardClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid="PromoOfferCard"
      >
        <a href={offerUrl} target="_blank" rel="noreferrer" className={styles['overlay']} />
        <div className={styles['content']}>
          <div className={styles['card-image']} style={{ backgroundImage: `url(${backgroundImage}` }}>
            <Avatar size={56} type="builder" src={logoUrl} />
          </div>
          <div
            onClick={onLabelClick}
            onMouseEnter={onLabelEnter}
            onMouseLeave={onLabelLeave}
            data-testid="PromoLabel"
            className={styles['promo-label']}
          >
            <Label
              background={labelColor}
              color="text-positive-default"
              rightIcon={<IconActionChevronRight12 color="current_color" />}
            >
              {promoTitle}
            </Label>
          </div>

          <div className={styles['text-content']}>
            {price}
            <div className={styles['text-ellipsis']}>
              <UIHeading5>{description}</UIHeading5>
            </div>
            <div className={styles['text-ellipsis']}>
              <UIText2>
                <Link
                  fontSize="S"
                  href={newbuildingUrl}
                  spacingSize="auto"
                  target="_blank"
                  onClick={trackNewbuildingClick}
                >
                  {newbuildingName}
                </Link>
              </UIText2>
            </div>
            <UIText2>{location}</UIText2>
          </div>
        </div>

        {isDesktop && (
          <div className={styles['controls']}>
            <CallButton size="M" theme="fill_secondary" onClick={onCallButtonClick} placeType="promoOffer" />
          </div>
        )}
      </div>
      <div className={styles['favorite-button']} data-testid="FavoriteButton">
        {favoriteButton}
      </div>
    </div>
  );
};
